import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { TeamSyncButton } from "@/components/fleet/TeamSyncButton";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DeadlinesTab } from "@/components/fleet/DeadlinesTab";
import { VehiclesTab } from "@/components/fleet/VehiclesTab";
import { MaintenanceTab } from "@/components/fleet/MaintenanceTab";
import { CostsTab } from "@/components/fleet/CostsTab";
import { EquipmentTab } from "@/components/fleet/EquipmentTab";
import { VehicleSheet } from "@/components/fleet/VehicleSheet";

export default function Fleet() {
  const [allVehicles, setVehicles] = useState([]);
  const { reload, inCompany } = useData();
  const vehicles = allVehicles.filter((v) => inCompany(v.company_id));
  const [refreshKey, setRefreshKey] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("deadlines");
  const [equipId, setEquipId] = useState(null);
  useEffect(() => {
    const id = params.get("vehicle"), eq = params.get("equipment");
    if (id) setOpenId(id);
    if (eq) { setTab("equipment"); setEquipId(eq); }
    if (id || eq) setParams({}, { replace: true });
  }, [params, setParams]);
  const load = useCallback(() => api.get("/fleet/vehicles").then((r) => setVehicles(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); setRefreshKey((k) => k + 1); };
  const { can } = useAuth();
  const live = vehicles.filter((v) => !["SCRAPPED", "RETURNED", "SOLD"].includes(v.status));
  return (
    <div data-testid="fleet-page">
      <PageHeader eyebrow="Mezzi e attrezzature" title="Flotta aziendale">
        <div className="flex flex-wrap items-center gap-4">
          <div className="font-mono text-sm" data-testid="fleet-counts">
            <b data-testid="fleet-count-live">{live.length}</b> mezzi in flotta · <b data-testid="fleet-count-team">{live.filter((v) => v.team_id).length}</b> assegnati a una squadra · <b data-testid="fleet-count-return">{live.filter((v) => v.status === "TO_RETURN").length}</b> da riconsegnare
          </div>
          {can("fleet:write") && <TeamSyncButton onDone={() => { changed(); reload?.(); }} />}
        </div>
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 h-auto flex-wrap">
          <TabsTrigger value="deadlines" data-testid="fleet-tab-deadlines">Scadenze</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="fleet-tab-vehicles">Mezzi</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="fleet-tab-maintenance">Manutenzioni</TabsTrigger>
          <TabsTrigger value="costs" data-testid="fleet-tab-costs">Costi</TabsTrigger>
          <TabsTrigger value="equipment" data-testid="fleet-tab-equipment">Attrezzature</TabsTrigger>
        </TabsList>
        <TabsContent value="deadlines"><DeadlinesTab refreshKey={refreshKey} onOpenVehicle={setOpenId} /></TabsContent>
        <TabsContent value="vehicles"><VehiclesTab vehicles={vehicles} onChanged={changed} onOpenVehicle={setOpenId} /></TabsContent>
        <TabsContent value="maintenance"><MaintenanceTab vehicles={live} refreshKey={refreshKey} onChanged={changed} /></TabsContent>
        <TabsContent value="costs"><CostsTab vehicles={vehicles} refreshKey={refreshKey} onChanged={changed} /></TabsContent>
        <TabsContent value="equipment"><EquipmentTab onChanged={changed} openId={equipId} onOpened={() => setEquipId(null)} /></TabsContent>
      </Tabs>
      <VehicleSheet vehicleId={openId} onClose={() => setOpenId(null)} refreshKey={refreshKey} />
    </div>
  );
}
